import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { attachOauth, deattachOauth, getOAuthApp } from 'client/api';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import AliceAppAccountLinkingForm from 'client/blocks-redesign/AliceAppAccountLinkingForm/AliceAppAccountLinkingForm';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { UpdateOAuthAppSettings } from 'client/model/oauth';
import { Channel, Skill } from 'client/model/skill';
import { getErrorMessage } from 'common/utils/errorsHandler';
import * as keyset from 'i18n/AccountLinking';
import stubTrue from 'lodash/stubTrue';

const t = i18n(keyset);

interface Props {
    skill: Skill;
    disabled: boolean;
}

const EMPTY_OAUTH_APP = {
    name: '',
    clientId: '',
    clientSecret: '',
    authorizationUrl: '',
    tokenUrl: '',
    refreshTokenUrl: '',
    scope: '',
    yandexClientId: '',
};

export const AliceAppAccountLinking: FC<Props> = ({ skill, disabled }) => {
    const aliceAppContext = useContext(CurrentAliceAppContext);
    const refreshAliceApp = aliceAppContext?.refreshAliceApp;

    const isAvailable =
        (skill.channel === Channel.AliceSkill && __appConfig.user.featureFlags.allowSetAccountLinkingForAliceSkills) ||
        skill.channel === Channel.SmartHome;

    const oauthAppId = skill.draft?.oauthAppId;

    const [isLoading, setIsLoading] = useState(Boolean(oauthAppId));
    const [oauthApp, setOauthApp] = useState<UpdateOAuthAppSettings>({
        ...EMPTY_OAUTH_APP,
    });

    const { add: addToast } = useToaster();

    const fetchOAuthApp = useCallback(
        async (id: string) => {
            try {
                setIsLoading(true);
                const app = await getOAuthApp(id);
                setOauthApp({
                    ...app,
                    clientSecret: '',
                    id: app.id,
                });
            } catch (error) {
                console.error('@fetchOAuthApp', error);
                addToast({
                    title: t('error-occured'),
                    theme: 'danger',
                    name: 'ACCOUNT_LINKING_SAVE_ERROR',
                });
            } finally {
                setIsLoading(false);
            }
        },
        [addToast],
    );

    useEffect(() => {
        if (isAvailable && oauthAppId) {
            void fetchOAuthApp(oauthAppId);
        }
    }, [isAvailable, oauthAppId, fetchOAuthApp]);

    const onChange = useCallback(
        async (id: string | null) => {
            try {
                if (!oauthAppId && id) {
                    await attachOauth(skill.id, id);
                }
                if (oauthAppId && !id) {
                    await deattachOauth(skill.id);
                    setOauthApp({ ...EMPTY_OAUTH_APP });
                }
                if (id) {
                    await fetchOAuthApp(id);
                }
                if (oauthAppId !== id) {
                    refreshAliceApp?.();
                }
            } catch (error) {
                console.error('@AliceAppAccountLinking onChange', error);
                const errorMessage = getErrorMessage(error);
                addToast({
                    title: errorMessage ?? t('error-occured'),
                    theme: 'danger',
                    name: 'ACCOUNT_LINKING_SAVE_ERROR',
                });
            }
        },
        [oauthAppId, skill.id, addToast, fetchOAuthApp, refreshAliceApp],
    );

    if (!isAvailable) {
        return null;
    }

    if (isLoading) return <PageLoader />;

    return (
        <ErrorBoundary withHomepageLink>
            <div>
                <AliceAppAccountLinkingForm
                    key={oauthApp.id}
                    skill={skill}
                    hasChanges={false}
                    registerChanges={stubTrue}
                    settings={oauthApp}
                    onUpdate={onChange}
                    name={skill.name || skill.draft.name}
                    disabled={disabled}
                />
            </div>
        </ErrorBoundary>
    );
};
